import React, { useMemo } from 'react';
import { SalesSummaryPageQuery } from '../../../../services';
import { formatFloat } from '../../../../helpers';
import { CTableWrapper } from '../../../dum';

type AuditSale =
    SalesSummaryPageQuery['getProductSalesSummary']['sales'][number];

type AuditSaleGroup = {
    id: number;
    orderCode: number | null;
    accountName: string | null;
    accountAbbreviation: string | null;
    receiptTypeName: string | null;
    products: AuditSale[];
    kilos: number;
    groups: number;
    total: number;
    tax: number;
    totalWithTax: number;
};

const moneyFormat = (value: number) => `$${formatFloat(value)}`;

export function SalesSummaryAuditTable({ sales }: { sales: AuditSale[] }) {
    // The summary query returns one row per sale × product. Collapse those rows
    // back to a sale-level audit view, retaining the original product rows for
    // the on-demand drill-down.
    const saleGroups = useMemo(() => {
        const groups: Record<number, AuditSaleGroup> = {};

        sales.forEach((sale, index) => {
            // The audit query always requests order_sale_id, but generated
            // GraphQL types mark it nullable. Keep a malformed row visible
            // rather than letting it prevent the entire audit from rendering.
            const saleId = sale.order_sale_id ?? -(index + 1);
            const existing = groups[saleId];
            if (existing) {
                existing.products.push(sale);
                existing.kilos += sale.kilos_sold;
                existing.groups += sale.groups_sold;
                existing.total += sale.total;
                existing.tax += sale.tax;
                existing.totalWithTax += sale.total_with_tax;
                return;
            }

            groups[saleId] = {
                id: saleId,
                orderCode: sale.order_code ?? null,
                accountName: sale.account_name ?? null,
                accountAbbreviation: sale.account_abbreviation ?? null,
                receiptTypeName: sale.receipt_type_name ?? null,
                products: [sale],
                kilos: sale.kilos_sold,
                groups: sale.groups_sold,
                total: sale.total,
                tax: sale.tax,
                totalWithTax: sale.total_with_tax,
            };
        });

        return Object.keys(groups).map((key) => groups[Number(key)]);
    }, [sales]);

    const totals = saleGroups.reduce(
        (sum, sale) => {
            sum.kilos += sale.kilos;
            sum.groups += sale.groups;
            sum.total += sale.total;
            sum.tax += sale.tax;
            sum.totalWithTax += sale.totalWithTax;
            return sum;
        },
        { kilos: 0, groups: 0, total: 0, tax: 0, totalWithTax: 0 },
    );

    return (
        <CTableWrapper<AuditSaleGroup>
            stickyHeader={false}
            items={[
                {
                    title: 'Venta',
                    content: (sale) => sale.orderCode ?? sale.id,
                },
                {
                    title: 'Cliente',
                    content: (sale) =>
                        sale.accountAbbreviation || sale.accountName || '—',
                    cellGrow: 2,
                },
                {
                    title: 'Comprobante',
                    content: (sale) => sale.receiptTypeName || '—',
                },
                {
                    title: 'Productos',
                    textAlign: 'right',
                    content: (sale) => sale.products.length,
                },
                {
                    title: 'Kilos',
                    textAlign: 'right',
                    content: (sale) => formatFloat(sale.kilos),
                },
                {
                    title: 'Grupos',
                    textAlign: 'right',
                    content: (sale) => formatFloat(sale.groups),
                },
                {
                    title: 'Subtotal',
                    textAlign: 'right',
                    content: (sale) => moneyFormat(sale.total),
                },
                {
                    title: 'IVA',
                    textAlign: 'right',
                    content: (sale) => moneyFormat(sale.tax),
                },
                {
                    title: 'Total',
                    textAlign: 'right',
                    content: (sale) => moneyFormat(sale.totalWithTax),
                },
            ]}
            data={saleGroups}
            collapse={(sale) => (
                <CTableWrapper<AuditSale>
                    stickyHeader={false}
                    items={[
                        {
                            title: 'Producto',
                            content: (product) =>
                                product.product_name || '—',
                            cellGrow: 3,
                        },
                        {
                            title: 'Kilos',
                            textAlign: 'right',
                            content: (product) =>
                                formatFloat(product.kilos_sold),
                        },
                        {
                            title: 'Grupos',
                            textAlign: 'right',
                            content: (product) =>
                                formatFloat(product.groups_sold),
                        },
                        {
                            title: 'Subtotal',
                            textAlign: 'right',
                            content: (product) => moneyFormat(product.total),
                        },
                        {
                            title: 'IVA',
                            textAlign: 'right',
                            content: (product) => moneyFormat(product.tax),
                        },
                        {
                            title: 'Total',
                            textAlign: 'right',
                            content: (product) =>
                                moneyFormat(product.total_with_tax),
                        },
                    ]}
                    data={sale.products}
                />
            )}
            totalsRow={[
                'Total',
                null,
                null,
                saleGroups.reduce(
                    (count, sale) => count + sale.products.length,
                    0,
                ),
                formatFloat(totals.kilos),
                formatFloat(totals.groups),
                moneyFormat(totals.total),
                moneyFormat(totals.tax),
                moneyFormat(totals.totalWithTax),
            ]}
        />
    );
}
